import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';

/**
 * Format a date to a human-readable string
 */
export function formatDate(date, formatStr = 'MMM d, yyyy h:mm a') {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? parseISO(date) : date;

  if (!isValid(dateObj)) return '';

  return format(dateObj, formatStr);
}

/**
 * Format a date to relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(date) {
  if (!date) return 'Never';

  const dateObj = typeof date === 'string' ? parseISO(date) : date;

  if (!isValid(dateObj)) return 'Never';

  return formatDistanceToNow(dateObj, { addSuffix: true });
}

/**
 * Format priority badge text
 */
export function formatPriority(priority) {
  const priorities = {
    critical: 'Critical',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
  };
  return priorities[priority] || priority;
}

/**
 * Format category badge text
 */
export function formatCategory(category) {
  const categories = {
    important: 'Important',
    work: 'Work',
    personal: 'Personal',
    finance: 'Finance',
    social: 'Social',
    promotional: 'Promotional',
    newsletter: 'Newsletter',
    receipt: 'Receipt',
    notification: 'Notification',
    spam: 'Spam',
    other: 'Other',
  };
  return categories[category] || category;
}

/**
 * Truncate text to specified length
 */
export function truncate(text, length = 100) {
  if (!text || text.length <= length) return text;
  return text.substring(0, length) + '...';
}

/**
 * Format email address (show name if available)
 */
export function formatEmailAddress(address) {
  if (!address) return '';

  // If it's a "Name <email@example.com>" format
  const match = address.match(/^(.+?)\s*<(.+?)>$/);
  if (match) {
    return match[1].trim();
  }

  return address;
}

/**
 * Get initials from email or name
 */
export function getInitials(text) {
  if (!text) return '?';

  // Extract name from "Name <email>" format
  const match = text.match(/^(.+?)\s*<.+>$/);
  const name = match ? match[1].trim() : text;

  // Split by spaces and get first letter of each word
  const words = name.split(/\s+/);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }

  // If single word or email, take first 2 characters
  return name.substring(0, 2).toUpperCase();
}

/**
 * Format file size
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Get color for priority badge
 */
export function getPriorityColor(priority) {
  const colors = {
    critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    high: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    low: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  };
  return colors[priority] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
}

/**
 * Get color for category badge
 */
export function getCategoryColor(category) {
  const colors = {
    important: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    work: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    personal: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    finance: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
    social: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    promotional: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
    newsletter: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
    receipt: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
    notification: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
    spam: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
    other: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200',
  };
  return colors[category] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
}
